import React, { memo, useCallback, useMemo, useState, useEffect } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import dayjs from 'dayjs'

interface ChangePositionModalProps {
  isModalOpen: boolean
  employeeId: number
  currentPostId?: number
  onOk?: (() => void) | undefined
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  enums?: any
}

interface IFormData {
  post_id?: number
  change_date?: string
  comment?: string
}

interface ISchemaActual {
  resultObject: Record<string, never>
  resultSchema: {
    properties: {
      post_id: any
      change_date: any
      comment: any
    }
    required: string[]
  }
}

export const ChangePositionModal: React.FC<ChangePositionModalProps> = memo(
  (props: ChangePositionModalProps) => {
    const { isModalOpen, employeeId, currentPostId, onOk, onCancel, enums } = props

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [formData, setFormData] = useState<IFormData>({
      post_id: undefined,
      change_date: undefined,
      comment: undefined,
    })

    const actualSchema = useMemo(() => {
      const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual

      if (schemaActual.resultSchema && schemaActual.resultSchema.properties) {
        // Добавляем enum для должности
        // Enum-ы приходят с бэкенда с ключом 'post' (без префикса 'hr_')
        if (enums?.post) {
          const oneOf = enums.post.map((item: any) => ({
            const: item.id,
            title: item.value,
          }))
          const enumValues = enums.post.map((item: any) => item.id)

          schemaActual.resultSchema.properties.post_id = {
            ...schemaActual.resultSchema.properties.post_id,
            type: 'string',
            oneOf,
            enum: enumValues,
            view: {
              name: 'select-from-enum',
            },
            options: {
              ...schemaActual.resultSchema.properties.post_id.options,
              allowClear: true,
              disabled: loading,
            },
          }
        }

        // Делаем все поля disabled при загрузке
        Object.keys(schemaActual.resultSchema.properties).forEach(key => {
          if (!schemaActual.resultSchema.properties[key].options) {
            schemaActual.resultSchema.properties[key].options = {}
          }
          schemaActual.resultSchema.properties[key].options.disabled = loading
        })
      }

      return schemaActual.resultSchema || {}
    }, [enums, loading])

    const handleOk = useCallback(async () => {
      const isRequiredFields = isFormDataRequired(formData, actualSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }

      try {
        setLoading(true)
        setError(null)
        await fetchAPI(`/api/hr/employees/${employeeId}/change-position`, {
          method: 'POST',
          body: JSON.stringify(formData),
        })
        onOk?.()
      } catch (err: any) {
        setError(err?.message || 'Не удалось изменить должность')
      } finally {
        setLoading(false)
      }
    }, [formData, actualSchema, employeeId, onOk])

    const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
      setFormData({
        post_id: undefined,
        change_date: undefined,
        comment: undefined,
      })
      setError(null)
      onCancel?.(e)
    }

    const onChange = useCallback(data => {
      setFormData(prevData => {
        return {
          ...prevData,
          ...data.formData,
        }
      })
    }, [])

    // Проверяем, равна ли выбранная должность текущей
    const isPostUnchanged = useMemo(() => {
      if (!formData.post_id || !currentPostId) {
        return false
      }
      // Приводим к числу для корректного сравнения (значения могут быть строками или числами)
      return Number(formData.post_id) === Number(currentPostId)
    }, [formData.post_id, currentPostId])

    // Устанавливаем значения по умолчанию при открытии модалки
    useEffect(() => {
      if (isModalOpen) {
        setFormData({
          post_id: currentPostId,
          change_date: dayjs().toISOString(),
          comment: undefined,
        })
        setError(null)
      }
    }, [isModalOpen, currentPostId])

    if (!isModalOpen) {
      return null
    }

    return (
      <Modal
        title={schema.title}
        onOk={handleOk}
        onCancel={handleCancel}
        open={isModalOpen}
        destroyOnHidden={true}
        okButtonProps={{
          disabled: loading || isPostUnchanged,
        }}
        cancelButtonProps={{
          disabled: loading,
        }}
        closable={!loading}
        keyboard={!loading}
        maskClosable={!loading}
        okText="Подтвердить"
      >
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        <Spin spinning={loading}>
          <DefaultJsonForm
            formData={formData}
            schema={actualSchema}
            onChange={onChange}
            orientation="horizontal"
          />
        </Spin>
      </Modal>
    )
  },
)

ChangePositionModal.displayName = 'ChangePositionModal'
